import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Modal, Input, List, Typography, Tag, Spin, Empty, App, type InputRef } from "antd";
import {
  ShopOutlined,
  AppstoreOutlined,
  SwapOutlined,
  DollarOutlined,
  FileTextOutlined,
  TeamOutlined,
  ContactsOutlined,
  BarcodeOutlined,
  CarOutlined,
  HomeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  AuditOutlined,
  FileDoneOutlined,
  SlidersOutlined,
  MailOutlined,
  FilterOutlined,
  BarChartOutlined,
  PlusOutlined,
  ArrowRightOutlined,
  MacCommandOutlined,
} from "@ant-design/icons";
import { useNavigate, Link } from "react-router";
import { useTranslation } from "react-i18next";
import { globalSearchDao } from "@services";
import { getEntityRoute, type EntityType } from "@ds/navigation/navigation";
import { LAYOUT_TOKENS } from "@ds/tokens/tokens";
import { TextValue } from "@ds/display/TextValue";
import { useCurrentEmployee } from "../../contexts/useCurrentEmployee";
import { ACCESS_GATES, type AccessGate } from "@ds/config/accessGates";

const { Text } = Typography;

// ─── Contact sublabel parser ────────────────────────────────────────────────

/** Parse contact sublabel packed as "Firma · phone · email · job · purpose1, purpose2" */
function parseContactSublabel(sublabel: string) {
  const parts = sublabel.split(" · ");
  const company = parts[0] || null;
  const phone   = parts.find(p => /^[+\d\s()-]{6,}$/.test(p.trim())) ?? null;
  const email   = parts.find(p => p.includes("@")) ?? null;
  const remaining = parts.slice(1).filter(p => p !== phone && p !== email);
  const job       = remaining[0] ?? null;
  const purposes  = remaining.slice(1).flatMap(p => p.split(",").map(s => s.trim())).filter(Boolean);
  return { company, phone, email, job, purposes };
}

// ─── Entity search config ────────────────────────────────────────────────────

interface SearchResult {
  entity: string;
  id: number;
  label: string;
  sublabel: string;
  rank: number;
}

interface EntityMeta {
  type: EntityType | null;
  icon: React.ReactNode;
  label: string;
  keywords: string[];
  color: string;
}

const ENTITY_CONFIG: Record<string, EntityMeta> = {
  companies:              { type: "company",     icon: <ShopOutlined />,      label: "Firma",       keywords: ["firma", "firmy", "firm", "spółka", "spolka"],                              color: "blue" },
  contacts:               { type: null,          icon: <ContactsOutlined />,  label: "Kontakt",     keywords: ["kontakt", "osoba", "kontakty"],                                            color: "default" },
  employees:              { type: "employee",    icon: <TeamOutlined />,      label: "Pracownik",   keywords: ["pracownik", "pracownicy", "employee"],                                      color: "purple" },
  products:               { type: "product",     icon: <AppstoreOutlined />,  label: "Produkt",     keywords: ["produkt", "produkty", "odpad", "odpady", "towar"],                         color: "cyan" },
  waste_codes:            { type: null,          icon: <BarcodeOutlined />,   label: "Kod odpadów", keywords: ["kod", "kody"],                                                             color: "default" },
  transactions:           { type: "transaction", icon: <SwapOutlined />,      label: "Transakcja",  keywords: ["transakcja", "transakcje", "zlecenie", "przewóz", "przewoz"],              color: "geekblue" },
  invoices:               { type: "invoice",     icon: <FileTextOutlined />,  label: "Faktura",     keywords: ["faktura", "faktury", "faktur", "fv", "invoice"],                           color: "gold" },
  price_lists:            { type: "price_list",  icon: <DollarOutlined />,    label: "Cennik",      keywords: ["cennik", "cena", "ceny", "price"],                                         color: "green" },
  transaction_transports: { type: "transaction", icon: <CarOutlined />,       label: "Transport",   keywords: ["transport", "kierowca", "rejestracja", "ciągnik", "ciagnik", "kontener"],  color: "orange" },
};

const ALL_ENTITIES = Object.keys(ENTITY_CONFIG);

// Keywords that activate command-only mode
const COMMAND_KEYWORDS = ["komenda", "komendy", "command"];

function getLastToken(raw: string): string {
  const tokens = raw.split(/\s+/);
  return tokens[tokens.length - 1] ?? "";
}

/** Returns entity key OR "commands" if the token matches a command keyword */
function findSuggestion(token: string): string | null {
  if (!token) return null;
  const lower = token.toLowerCase();
  // Check command keywords first
  const cmdKw = COMMAND_KEYWORDS.find((kw) => kw.startsWith(lower) && kw !== lower);
  if (cmdKw) return "__commands__";
  return ALL_ENTITIES.find((key) =>
    ENTITY_CONFIG[key].keywords.some((kw) => kw.startsWith(lower) && kw !== lower)
  ) ?? null;
}

function getCompletion(token: string): string | null {
  if (!token) return null;
  const lower = token.toLowerCase();
  // Check command keywords first
  const cmdKw = COMMAND_KEYWORDS.find((kw) => kw.startsWith(lower) && kw !== lower);
  if (cmdKw) return cmdKw;
  for (const key of ALL_ENTITIES) {
    const kw = ENTITY_CONFIG[key].keywords.find((k) => k.startsWith(lower) && k !== lower);
    if (kw) return kw;
  }
  return null;
}

function stripEntityKeywords(raw: string): string {
  const allKeywords = [...ALL_ENTITIES.flatMap((k) => ENTITY_CONFIG[k].keywords), ...COMMAND_KEYWORDS];
  return raw
    .split(/\s+/)
    .filter((tok) => !allKeywords.includes(tok.toLowerCase()))
    .join(" ")
    .trim();
}

function detectActiveFromQuery(raw: string): string[] {
  const tokens = raw.toLowerCase().split(/\s+/).filter(Boolean);
  return ALL_ENTITIES.filter((key) =>
    ENTITY_CONFIG[key].keywords.some((kw) => tokens.includes(kw))
  );
}

function detectCommandsFromQuery(raw: string): boolean {
  const tokens = raw.toLowerCase().split(/\s+/).filter(Boolean);
  return COMMAND_KEYWORDS.some((kw) => tokens.includes(kw));
}

// ─── Command config ──────────────────────────────────────────────────────────

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  category: "navigate" | "create";
  keywords: string[];
  path?: string;
  gate?: AccessGate;
  /** populated at render time for create-modal commands */
  action?: () => void;
}

const NAV_COMMANDS: CommandItem[] = [
  { id: "nav-dashboard",          label: "Strona główna",           icon: <HomeOutlined />,              category: "navigate", path: "/",                                keywords: ["dashboard", "główna", "home", "start"] },
  { id: "nav-transactions",       label: "Transakcje",              icon: <SwapOutlined />,              category: "navigate", path: "/transactions",                    keywords: ["transakcje", "transakcja", "zlecenia"] },
  { id: "nav-companies",          label: "Firmy",                   icon: <ShopOutlined />,              category: "navigate", path: "/companies",                       keywords: ["firmy", "firma", "kontrahenci"] },
  { id: "nav-products",           label: "Produkty",                icon: <AppstoreOutlined />,          category: "navigate", path: "/products",                        keywords: ["produkty", "produkt", "odpady"] },
  { id: "nav-price-lists",        label: "Cenniki",                 icon: <DollarOutlined />,            category: "navigate", path: "/price-lists",                     keywords: ["cenniki", "cennik", "ceny"], gate: "price_list_show" },
  { id: "nav-invoices-issued",    label: "Faktury przychodowe",     icon: <ArrowUpOutlined />,           category: "navigate", path: "/invoices/issued",                 keywords: ["faktury przychodowe", "sprzedaż", "issued"], gate: "invoice_show" },
  { id: "nav-invoices-received",  label: "Faktury kosztowe",        icon: <ArrowDownOutlined />,         category: "navigate", path: "/invoices/received",               keywords: ["faktury kosztowe", "koszty", "received"], gate: "invoice_show" },
  { id: "nav-edpr",               label: "EDPR",                    icon: <AuditOutlined />,             category: "navigate", path: "/edpr",                            keywords: ["edpr", "ewidencja"], gate: "edpr_show" },
  { id: "nav-pipeline",           label: "Dokumenty",               icon: <FileDoneOutlined />,          category: "navigate", path: "/dokumenty",                       keywords: ["dokumenty", "pipeline", "skany", "zadania"] },
  { id: "nav-employees",          label: "Pracownicy",              icon: <TeamOutlined />,              category: "navigate", path: "/settings/employees",              keywords: ["pracownicy", "pracownik"], gate: "employee_show" },
  { id: "nav-rules",              label: "Reguły transakcji",       icon: <FilterOutlined />,            category: "navigate", path: "/settings/rules",                  keywords: ["reguły", "reguła", "zasady"], gate: "settings_show" },
  { id: "nav-templates",          label: "Szablony dokumentów",     icon: <MailOutlined />,              category: "navigate", path: "/settings/template-configs",       keywords: ["szablony", "szablon", "email"], gate: "template_show" },
  { id: "nav-config",             label: "Konfiguracja",            icon: <SlidersOutlined />,           category: "navigate", path: "/settings/configuration",          keywords: ["konfiguracja", "ustawienia", "config"], gate: "settings_show" },
  { id: "nav-reports",            label: "Raporty",                 icon: <BarChartOutlined />,          category: "navigate", path: "/settings/reports",                keywords: ["raporty", "raport", "wykresy"], gate: "reports_show" },
];

const CREATE_COMMANDS: CommandItem[] = [
  { id: "create-transaction",        label: "Nowa transakcja",                  icon: <SwapOutlined />,      category: "create", keywords: ["nowa transakcja", "dodaj transakcję", "utwórz transakcję"] },
  { id: "create-company",            label: "Nowa firma",                       icon: <ShopOutlined />,      category: "create", keywords: ["nowa firma", "dodaj firmę", "utwórz firmę"] },
  { id: "create-edpr-request",       label: "Nowy wniosek EDPR",                icon: <AuditOutlined />,     category: "create", path: "/edpr-requests?id=new", keywords: ["nowy edpr", "ewidencja", "wniosek"], gate: "edpr_edit" },
  { id: "create-rule",               label: "Nowa reguła",                      icon: <FilterOutlined />,    category: "create", path: "/settings/rules/create",     keywords: ["nowa reguła", "dodaj regułę"], gate: "settings_edit" },
  { id: "create-template",           label: "Nowy szablon",                     icon: <MailOutlined />,      category: "create", path: "/settings/template-configs/create", keywords: ["nowy szablon", "dodaj szablon"], gate: "template_edit" },
];

// ─── Props ───────────────────────────────────────────────────────────────────

interface GlobalSearchModalProps {
  open: boolean;
  onClose: () => void;
  onCreateTransaction: () => void;
  onCreateCompany: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({
  open,
  onClose,
  onCreateTransaction,
  onCreateCompany,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { hasRole } = useCurrentEmployee();

  const [query, setQuery] = useState("");
  const [activeEntities, setActiveEntities] = useState<string[]>([]);
  const [commandsActive, setCommandsActive] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeCommandIdx, setActiveCommandIdx] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<InputRef | null>(null);
  const commandListRef = useRef<HTMLDivElement | null>(null);

  const checkGate = useCallback(
    (gate: AccessGate) => hasRole(...ACCESS_GATES[gate]),
    [hasRole],
  );

  // ── Tab autocomplete ──────────────────────────────────────────────────────
  const lastToken = useMemo(() => getLastToken(query), [query]);
  const tabSuggestionKey = useMemo(() => findSuggestion(lastToken), [lastToken]);
  const tabCompletion = useMemo(() => getCompletion(lastToken), [lastToken]);
  const ghostSuffix = tabCompletion ? tabCompletion.slice(lastToken.length) : null;

  // ── Commands (filtered by role + query) ───────────────────────────────────
  const allCommands = useMemo((): CommandItem[] => {
    const createActions: Record<string, () => void> = {
      "create-transaction": onCreateTransaction,
      "create-company":     onCreateCompany,
    };
    return [...NAV_COMMANDS, ...CREATE_COMMANDS]
      .filter((cmd) => (cmd.gate ? checkGate(cmd.gate) : true))
      .map((cmd) => ({
        ...cmd,
        action: createActions[cmd.id] ?? cmd.action,
      }));
  }, [checkGate, onCreateTransaction, onCreateCompany]);

  const filteredCommands = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allCommands;
    return allCommands.filter((cmd) => {
      const haystack = [cmd.label, cmd.description ?? "", ...cmd.keywords].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [query, allCommands]);

  const navCommands  = useMemo(() => filteredCommands.filter(c => c.category === "navigate"), [filteredCommands]);
  const createCommands = useMemo(() => filteredCommands.filter(c => c.category === "create"), [filteredCommands]);
  const flatCommands  = useMemo(() => [...navCommands, ...createCommands], [navCommands, createCommands]);

  // ── Entity search ─────────────────────────────────────────────────────────
  const runSearch = useCallback(async (q: string, entities: string[] | null) => {
    const trimmed = q.trim();
    if (!trimmed) { setResults([]); return; }
    setLoading(true);
    try {
      const data = await globalSearchDao.search(trimmed, entities ?? undefined, entities?.length === 1 ? 20 : 7);
      setResults(data);
    } catch {
      // soft-fail; modal stays empty
    } finally {
      setLoading(false);
    }
  }, []);

  const triggerSearch = useCallback((cleanQ: string, entities: string[] | null, skipIfCommandsActive = false) => {
    if (skipIfCommandsActive) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(cleanQ, entities), 300);
  }, [runSearch]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setActiveCommandIdx(0);
    const detectedCommands = detectCommandsFromQuery(val);
    if (detectedCommands) setCommandsActive(true);
    const detectedFromQuery = detectActiveFromQuery(val);
    setActiveEntities((prev) => {
      const merged = Array.from(new Set([...prev, ...detectedFromQuery]));
      const clean = stripEntityKeywords(val);
      triggerSearch(clean, merged.length > 0 ? merged : null, detectedCommands || commandsActive);
      return merged;
    });
    // `commandsActive` MUST be a dependency: without it this callback is built
    // once (its only other dep, `triggerSearch`, is itself stable) and the
    // captured `commandsActive` stays pinned to its mount value of `false` —
    // so toggling the "Komendy" tag never suppressed the entity search, even
    // though the toggle clears `results` on activation. Re-creating this
    // callback is free: it is only passed as the input's `onChange` and
    // appears in no dependency array, so a new identity triggers nothing.
  }, [triggerSearch, commandsActive]);

  // ── Keyboard handling ─────────────────────────────────────────────────────
  const handleCommandSelect = useCallback((cmd: CommandItem) => {
    onClose();
    if (cmd.action) {
      setTimeout(cmd.action, 50);
    } else if (cmd.path) {
      navigate(cmd.path);
    }
  }, [onClose, navigate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab") {
      e.preventDefault();
      if (tabSuggestionKey && tabCompletion) {
        const tokens = query.split(/\s+/);
        tokens[tokens.length - 1] = "";
        const newQuery = tokens.join(" ").trim() + " ";
        setQuery(newQuery);
        if (tabSuggestionKey === "__commands__") {
          setCommandsActive(true);
          setResults([]);
        } else {
          setActiveEntities((prev) => {
            const next = prev.includes(tabSuggestionKey) ? prev : [...prev, tabSuggestionKey];
            const clean = stripEntityKeywords(newQuery);
            triggerSearch(clean, next.length > 0 ? next : null);
            return next;
          });
        }
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveCommandIdx((i) => Math.min(i + 1, flatCommands.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveCommandIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && flatCommands.length > 0 && results.length === 0) {
      e.preventDefault();
      if (flatCommands[activeCommandIdx]) handleCommandSelect(flatCommands[activeCommandIdx]);
    }
  }, [query, tabSuggestionKey, tabCompletion, triggerSearch, flatCommands, activeCommandIdx, results.length, handleCommandSelect]);

  // Scroll active command into view
  useEffect(() => {
    const el = commandListRef.current?.querySelector(`[data-cidx="${activeCommandIdx}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeCommandIdx]);

  // ── Entity tag toggle ─────────────────────────────────────────────────────
  const toggleEntity = useCallback((key: string) => {
    setActiveEntities((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      const clean = stripEntityKeywords(query);
      triggerSearch(clean, next.length > 0 ? next : null);
      return next;
    });
    inputRef.current?.focus();
  }, [query, triggerSearch]);

  // ── Reset on close ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveEntities([]);
      setCommandsActive(false);
      setResults([]);
      setActiveCommandIdx(0);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, [open]);

  const cleanQuery = stripEntityKeywords(query);
  const canOpen = (entity: string) => !!ENTITY_CONFIG[entity]?.type;
  // Show commands when: commandsActive OR no DB results yet
  const showCommands = commandsActive || results.length === 0;

  // ─── Command group renderer ───────────────────────────────────────────────
  const renderCommandGroup = (label: string, items: CommandItem[], offset: number) => {
    if (!items.length) return null;
    return (
      <div>
        <div style={{
          padding: `${LAYOUT_TOKENS.spacingXs}px ${LAYOUT_TOKENS.spacingDefault}px`,
          fontSize: LAYOUT_TOKENS.fontSizeLabel,
          fontWeight: 600,
          color: LAYOUT_TOKENS.iconMuted,
          textTransform: "uppercase" as const,
          letterSpacing: "0.08em",
          borderTop: offset > 0 ? `1px solid ${LAYOUT_TOKENS.borderDefault}` : undefined,
        }}>
          {label}
        </div>
        {items.map((cmd, i) => {
          const idx = offset + i;
          const isActive = activeCommandIdx === idx;
          return (
            <div
              key={cmd.id}
              data-cidx={idx}
              onClick={() => handleCommandSelect(cmd)}
              onMouseEnter={() => setActiveCommandIdx(idx)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: LAYOUT_TOKENS.spacingSmall,
                padding: `${LAYOUT_TOKENS.spacingSmall}px ${LAYOUT_TOKENS.spacingDefault}px`,
                cursor: "pointer",
                background: isActive ? LAYOUT_TOKENS.surfaceSubtle : undefined,
                transition: "background 0.1s",
              }}
            >
              <span style={{ color: LAYOUT_TOKENS.iconNeutral, fontSize: LAYOUT_TOKENS.fontSizeNormal, width: 16, flexShrink: 0 }}>
                {cmd.icon}
              </span>
              <Text style={{ flex: 1, fontSize: LAYOUT_TOKENS.fontSizeBody }}>{cmd.label}</Text>
              <span style={{ color: LAYOUT_TOKENS.iconMuted, fontSize: LAYOUT_TOKENS.fontSizeSmall, flexShrink: 0 }}>
                {cmd.category === "create" ? <PlusOutlined /> : <ArrowRightOutlined />}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      width={700}
      styles={{ body: { padding: 0 } }}
      destroyOnHidden
      afterOpenChange={(visible) => { if (visible) inputRef.current?.focus(); }}
    >
      {/* ── Input with inline ghost completion ── */}
      <div style={{ padding: `${LAYOUT_TOKENS.spacingDefault}px ${LAYOUT_TOKENS.spacingDefault}px ${LAYOUT_TOKENS.spacingSmall}px`, position: "relative" }}>
        {ghostSuffix && (
          <div aria-hidden style={{
            position: "absolute",
            top: LAYOUT_TOKENS.spacingDefault,
            left: LAYOUT_TOKENS.spacingDefault,
            right: LAYOUT_TOKENS.spacingDefault,
            bottom: LAYOUT_TOKENS.spacingSmall,
            padding: "8px 11px",
            fontSize: LAYOUT_TOKENS.fontSizeSubheading,
            lineHeight: "1.5",
            fontFamily: "inherit",
            whiteSpace: "pre",
            overflow: "hidden",
            pointerEvents: "none",
            borderRadius: LAYOUT_TOKENS.radiusSm,
            border: "1px solid transparent",
          }}>
            <span style={{ color: "transparent" }}>{query}</span>
            <span style={{ color: LAYOUT_TOKENS.iconNeutral }}>{ghostSuffix}</span>
            <kbd style={{ marginLeft: 4, fontSize: LAYOUT_TOKENS.fontSizeMicro, color: LAYOUT_TOKENS.iconNeutral, opacity: 0.7, background: LAYOUT_TOKENS.surfaceSubtle, border: `1px solid ${LAYOUT_TOKENS.borderDefault}`, borderRadius: LAYOUT_TOKENS.radiusXs, padding: "0 3px" }}>Tab</kbd>
          </div>
        )}
        <Input
          ref={inputRef}
          size="large"
          placeholder={ghostSuffix ? undefined : "Szukaj lub wpisz komendę…"}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          allowClear
          onClear={() => { setQuery(""); setResults([]); setActiveCommandIdx(0); setCommandsActive(false); }}
          suffix={loading ? <Spin size="small" /> : undefined}
          style={{ background: "transparent", position: "relative" }}
        />
      </div>

      {/* ── Entity filter tags ── */}
      <div style={{
        padding: `0 ${LAYOUT_TOKENS.spacingDefault}px ${LAYOUT_TOKENS.spacingSmall}px`,
        display: "flex",
        flexWrap: "wrap",
        gap: LAYOUT_TOKENS.spacingXs,
      }}>
        {/* Commands mode tag */}
        <Tag
          icon={<MacCommandOutlined />}
          color={commandsActive ? "volcano" : undefined}
          onClick={() => {
            const next = !commandsActive;
            setCommandsActive(next);
            if (!next) {
              const clean = stripEntityKeywords(query);
              triggerSearch(clean, activeEntities.length > 0 ? activeEntities : null);
            } else {
              setResults([]);
            }
            inputRef.current?.focus();
          }}
          style={{
            cursor: "pointer",
            margin: 0,
            opacity: commandsActive ? 1 : 0.5,
            borderStyle: tabSuggestionKey === "__commands__" && !commandsActive ? "dashed" : "solid",
            fontWeight: commandsActive ? 500 : 400,
            transition: "all 0.15s",
          }}
        >
          Komendy
        </Tag>
        {ALL_ENTITIES.map((key) => {
          const cfg = ENTITY_CONFIG[key];
          const isActive = activeEntities.includes(key);
          const isSuggested = key === tabSuggestionKey && !isActive;
          return (
            <Tag
              key={key}
              color={isActive ? cfg.color : undefined}
              icon={cfg.icon}
              onClick={() => toggleEntity(key)}
              style={{
                cursor: "pointer",
                margin: 0,
                opacity: isActive ? 1 : 0.5,
                borderStyle: isSuggested ? "dashed" : "solid",
                fontWeight: isActive ? 500 : 400,
                transition: "all 0.15s",
              }}
            >
              {cfg.label}
            </Tag>
          );
        })}
      </div>

      {/* ── Commands section (shown when no DB results) ── */}
      {showCommands && (
        <div
          ref={commandListRef}
          style={{ borderTop: `1px solid ${LAYOUT_TOKENS.borderDefault}`, maxHeight: 400, overflowY: "auto" }}
        >
          {flatCommands.length === 0 ? (
            <Empty
              description={t("common.messages.noResults")}
              style={{ padding: `${LAYOUT_TOKENS.spacingLarge}px 0` }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <>
              {renderCommandGroup("Nawigacja", navCommands, 0)}
              {renderCommandGroup("Utwórz", createCommands, navCommands.length)}
            </>
          )}
        </div>
      )}

      {/* ── Entity search results ── */}
      {results.length > 0 && (
        <List
          dataSource={results}
          style={{ maxHeight: 400, overflowY: "auto", borderTop: `1px solid ${LAYOUT_TOKENS.borderDefault}` }}
          renderItem={(item) => {
            const config = ENTITY_CONFIG[item.entity];
            const clickable = canOpen(item.entity);
            const href = clickable && config?.type ? getEntityRoute(config.type, item.id) : null;
            const rowBody = (
                <div style={{ display: "flex", alignItems: "flex-start", gap: LAYOUT_TOKENS.spacingSmall, flex: 1, minWidth: 0 }}>
                  <Tag
                    color={config?.color}
                    icon={config?.icon}
                    style={{ flexShrink: 0, margin: 0, marginTop: 1, fontSize: LAYOUT_TOKENS.fontSizeSmall }}
                  >
                    {config?.label ?? item.entity}
                  </Tag>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.label}
                    </Text>
                    {item.entity === "contacts" && item.sublabel ? (() => {
                      const { company, phone, email, job, purposes } = parseContactSublabel(item.sublabel);
                      return (
                        <div style={{ fontSize: LAYOUT_TOKENS.fontSizeSmall, color: LAYOUT_TOKENS.iconNeutral, marginTop: 2 }}>
                          {company && <span style={{ marginRight: 8 }}>{company}</span>}
                          {phone && (
                            <a href={`tel:${phone}`} onClick={(e) => e.stopPropagation()} style={{ marginRight: 8, color: LAYOUT_TOKENS.iconNeutral }}>
                              {phone}
                            </a>
                          )}
                          {email && (
                            <span style={{ marginRight: 8 }}>
                              <TextValue
                                underline="dotted"
                                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(email); message.success("Skopiowano"); }}
                              >
                                {email}
                              </TextValue>
                            </span>
                          )}
                          {job && <span style={{ marginRight: 8 }}>{job}</span>}
                          {purposes.map((p) => (
                            <Tag key={p} style={{ fontSize: LAYOUT_TOKENS.fontSizeMicro, padding: "0 4px", margin: "0 2px" }}>{p}</Tag>
                          ))}
                        </div>
                      );
                    })() : item.sublabel && (
                      <Text type="secondary" style={{ fontSize: LAYOUT_TOKENS.fontSizeSmall, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.sublabel}
                      </Text>
                    )}
                  </div>
                </div>
            );
            return (
              <List.Item
                key={`${item.entity}-${item.id}`}
                style={{
                  padding: `${LAYOUT_TOKENS.spacingSmall}px ${LAYOUT_TOKENS.spacingDefault}px`,
                  cursor: clickable ? "pointer" : "default",
                  gap: LAYOUT_TOKENS.spacingSmall,
                  alignItems: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => { if (clickable) (e.currentTarget as HTMLElement).style.background = LAYOUT_TOKENS.surfaceSubtle; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}
              >
                {href ? (
                  <Link
                    to={href}
                    onClick={onClose}
                    style={{ display: "flex", flex: 1, minWidth: 0, color: "inherit" }}
                  >
                    {rowBody}
                  </Link>
                ) : rowBody}
                {!clickable && item.entity !== "contacts" && (
                  <Text type="secondary" style={{ fontSize: LAYOUT_TOKENS.fontSizeSmall, flexShrink: 0 }}>
                    {t("common.globalSearch.noDrawer")}
                  </Text>
                )}
              </List.Item>
            );
          }}
        />
      )}

      {/* ── No results state (search returned nothing) ── */}
      {!loading && cleanQuery && results.length === 0 && filteredCommands.length === 0 && (
        <Empty
          description={t("common.messages.noResults")}
          style={{ padding: `${LAYOUT_TOKENS.spacingLarge}px 0` }}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </Modal>
  );
};
